import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation } from "@tanstack/react-query";
import { showToast } from "../../../store/toast/toastSlice";
import {
  deleteDeliverableFile,
  downloadWorkOrderFiles,
  uploadDeliverableFiles,
} from "../../../services/api/workOrder";
import {
  removeDeliverableFileById,
  updateWorkOrderForm,
} from "../../../store/workOrder/workOrderSlice";
import { UserType } from "../../../utils/enum";
import useAuth from "../../../hooks/useAuth";
import { useParams } from "react-router-dom";
import { getFileIconClass, isImage } from "../../../utils/helpers";
import { saveAs } from "file-saver";
import BaseTooltip from "../../../components/ui/BaseTooltip";

const Deliverables = ({ isAssignedEngineer, workOrderDeliverables }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { authRole } = useAuth();
  const fileInputRef = useRef(null);
  const [isDeliverableLimitWarning, setIsDeliverableLimitWarning] = useState(false);

  const deliverables = useSelector((state) => state.workOrder.summary.deliverables);
  const fileList = deliverables?.files;
  useEffect(() => {
    if (id && workOrderDeliverables) {
      dispatch(
        updateWorkOrderForm({
          step: "summary",
          data: {
            deliverables: {
              files: workOrderDeliverables,
            },
          },
        }),
      );
    }
  }, [id, workOrderDeliverables]);

  const { isPending: isUploadingDeliverables, mutateAsync: uploadDeliverablesMutation } =
    useMutation({
      mutationFn: ({ workOrderId, formData }) => uploadDeliverableFiles(workOrderId, formData),
    });

  const { isPending: isDeletingDeliverable, mutateAsync: deleteDeliverableMutation } = useMutation({
    mutationFn: deleteDeliverableFile,
  });
  const {
    isLoading: isDownloadLoading,
    error: downloadError,
    data: zipBlobData,
    mutateAsync: downloadZip,
  } = useMutation({
    mutationFn: downloadWorkOrderFiles,
    onSuccess: (zipBlob) => {
      const fileName = `workorder-${id}-deliverables.zip`;
      saveAs(zipBlob, fileName);
    },
    onError: (error) => {
      console.error("Failed to download zip:", error);
    },
  });

  const handleFileChange = async (e) => {
    let files = Array.from(e.target.files);
    if (!files.length) return;
    let length = files.length > 5;

    setIsDeliverableLimitWarning("");

    if (length) {
      // setdeliverableLimitWarning("Only first 5 files uploaded");
      setIsDeliverableLimitWarning(true);
      files = files.slice(0, 5);
    } else {
      setIsDeliverableLimitWarning(false);
    }
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const uploaded = await uploadDeliverablesMutation({
        workOrderId: id,
        formData,
      });
      const newFiles = uploaded.data.files.map((file) => ({
        id: file.id,
        filename: file.fileName,
        url: file.url,
      }));
      dispatch(
        updateWorkOrderForm({
          step: "summary",
          data: {
            deliverables: {
              ...deliverables,
              files: [...(fileList || []), ...newFiles],
            },
          },
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
    e.target.value = "";
  };

  const handleRemoveFile = async (file) => {
    try {
      await deleteDeliverableMutation(file.id);
      dispatch(removeDeliverableFileById(file.id));
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const handleDownloadZip = async () => {
    try {
      await downloadZip(id);
    } catch (error) {
      console.error("Failed to download zip:", error);
    }
  };

  return (
    <BaseCard className="pt-4 pb-6 xl:px-16 px-5 rounded-lg !mb-[0px]" elevation={1}>
      <div className="border-bottom mb-4 flex items-center flex">
        <h6 className="mb-1 text-dark">Deliverables</h6>

        {fileList.length > 0 && (
          <BaseTooltip content="Download Zipped Files" placement="bottom">
            <i className="ml-3 fa-solid fa-download cursor-pointer" onClick={handleDownloadZip}></i>
          </BaseTooltip>
        )}
      </div>

      {authRole !== UserType.CLIENT && isAssignedEngineer && (
        <>
          <div className="flex items-center mt-4 space-x-2 cursor-pointer">
            <div onClick={() => fileInputRef.current?.click()} className="flex items-center">
              <i className="fa-solid fa-plus text-secondary"></i>
              <p className="text-secondary ml-1"> Add Files</p>
            </div>
            <p className=" ml-1">(Max 5 at a time!)</p>

            {isUploadingDeliverables && (
              <div className="ml-2 animate-spin text-primary">
                <i className="fa-solid fa-spinner"></i>
              </div>
            )}
            <input type="file" multiple hidden ref={fileInputRef} onChange={handleFileChange} />
          </div>
          {isDeliverableLimitWarning && <p class="text-red-500">Only first 5 files uploaded</p>}
        </>
      )}

      <div className="grid grid-cols-2 gap-4 py-3">
        {[...fileList]?.reverse().map((file, index) => (
          <div key={index} className="bg-[#F6F6F6] rounded-md mt-4">
            <div className="flex justify-between px-4 py-2 items-center">
              <div className="flex items-center">
                {isImage(file.filename) ? (
                  <img
                    className="h-14 w-14 rounded-md object-cover"
                    src={file.url}
                    alt={file.filename}
                  />
                ) : (
                  <div className="h-14 w-14 rounded-md bg-gray-100 flex items-center justify-center">
                    <i className={`fa-solid ${getFileIconClass(file.filename)} text-[40px]`} />
                  </div>
                )}
                <div className="ml-3">
                  <p className="font-normal">{file.filename}</p>
                </div>
              </div>

              <div className="flex items-center">
                <i
                  className="fa-solid fa-eye cursor-pointer"
                  title="View File"
                  onClick={() => window.open(file.url, "_blank")}
                ></i>

                {authRole !== UserType.CLIENT && isAssignedEngineer && (
                  <i
                    className="fa-regular fa-trash-can ml-3 cursor-pointer"
                    title="Delete File"
                    onClick={() => handleRemoveFile(file)}
                  ></i>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </BaseCard>
  );
};

export default Deliverables;
